import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Plus, GripVertical, Image, ChevronDown } from 'lucide-react';
import { WorkflowStep, Workflow } from '@/types/workflow';
import { FormField } from '@ponder/artifacts/conversation-ui';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TransitionEditor } from './TransitionEditor';
import { ImageGenerationDialog } from './ImageGenerationDialog';

interface WorkflowStepEditorProps {
  step: WorkflowStep;
  onChange: (step: WorkflowStep) => void;
  onDelete: () => void;
  dragHandleProps?: any;
  availableSteps: string[];
  availableWorkflows: Workflow[];
}

const FIELD_TYPES = [
  'singleChoice',
  'multiChoice',
  'dateTime',
  'textInput',
  'number'
] as const;

export const WorkflowStepEditor: React.FC<WorkflowStepEditorProps> = ({
  step,
  onChange,
  onDelete,
  dragHandleProps,
  availableSteps,
  availableWorkflows
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [fieldMode, setFieldMode] = useState<'static' | 'agent'>(
    step.agentTask ? 'agent' : 'static'
  );
  const [useMultipleFields, setUseMultipleFields] = useState<boolean>(
    !!step.fields && step.fields.length > 0
  );
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  const handleFieldChange = (fieldIndex: number, field: Partial<FormField>) => {
    if (useMultipleFields) {
      const updatedFields = [...(step.fields || [])];
      updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...field } as FormField;
      console.log('[WorkflowStepEditor] Updating fields:', { fieldIndex, field, updatedFields });
      onChange({ ...step, fields: updatedFields });
    } else {
      onChange({
        ...step,
        field: { ...step.field!, ...field } as FormField
      });
    }
  };

  const handleAgentTaskChange = (agentTask: Partial<WorkflowStep['agentTask']>) => {
    onChange({
      ...step,
      agentTask: { ...step.agentTask!, ...agentTask } as WorkflowStep['agentTask']
    });
  };

  const addOption = (fieldIndex: number) => {
    const currentField = useMultipleFields ? step.fields?.[fieldIndex] : step.field;
    if (!currentField || (currentField.type !== 'singleChoice' && currentField.type !== 'multiChoice')) return;

    const updatedField = {
      ...currentField,
      options: [
        ...(currentField.options || []),
        { value: '', label: '' }
      ]
    };

    if (useMultipleFields) {
      const updatedFields = [...(step.fields || [])];
      updatedFields[fieldIndex] = updatedField as FormField;
      onChange({ ...step, fields: updatedFields });
    } else {
      onChange({ ...step, field: updatedField as FormField });
    }
  };

  const updateOption = (fieldIndex: number, optionIndex: number, updates: { value?: string; label?: string; icon?: string; description?: string }) => {
    const currentField = useMultipleFields ? step.fields?.[fieldIndex] : step.field;
    if (!currentField || (currentField.type !== 'singleChoice' && currentField.type !== 'multiChoice')) return;

    const options = [...(currentField.options || [])];
    options[optionIndex] = { ...options[optionIndex], ...updates };

    const updatedField = { ...currentField, options } as FormField;

    if (useMultipleFields) {
      const updatedFields = [...(step.fields || [])];
      updatedFields[fieldIndex] = updatedField;
      onChange({ ...step, fields: updatedFields });
    } else {
      onChange({ ...step, field: updatedField });
    }
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const currentField = useMultipleFields ? step.fields?.[fieldIndex] : step.field;
    if (!currentField || (currentField.type !== 'singleChoice' && currentField.type !== 'multiChoice')) return;
    if (!currentField.options) return;

    const options = currentField.options.filter((_: any, i: number) => i !== optionIndex);
    const updatedField = { ...currentField, options } as FormField;

    if (useMultipleFields) {
      const updatedFields = [...(step.fields || [])];
      updatedFields[fieldIndex] = updatedField;
      onChange({ ...step, fields: updatedFields });
    } else {
      onChange({ ...step, field: updatedField });
    }
  };

  const createFieldByType = (type: typeof FIELD_TYPES[number], fieldId?: string): FormField => {
    const baseField = {
      id: fieldId || step.id,
      type,
      label: '',
      required: false
    };

    switch (type) {
      case 'singleChoice':
      case 'multiChoice':
        return { ...baseField, type, options: [] } as FormField;
      case 'dateTime':
        return { ...baseField, type, includeTime: false } as FormField;
      case 'textInput':
        return { ...baseField, type, multiline: false } as FormField;
      case 'number':
        return { ...baseField, type } as FormField;
      default:
        return baseField as FormField;
    }
  };

  const initializeField = (type: typeof FIELD_TYPES[number]) => {
    const field = createFieldByType(type);
    const { agentTask, fields, ...stepWithoutAgentAndFields } = step;
    onChange({ ...stepWithoutAgentAndFields, field } as WorkflowStep);
  };

  const addNewField = () => {
    const newField = createFieldByType('textInput', `${step.id}_field${(step.fields?.length || 0) + 1}`);
    const updatedFields = [...(step.fields || []), newField];
    const { field, ...stepWithoutField } = step;
    console.log('[WorkflowStepEditor] Adding new field:', { newField, updatedFields });
    onChange({ ...stepWithoutField, fields: updatedFields } as WorkflowStep);
  };

  const removeField = (fieldIndex: number) => {
    const updatedFields = step.fields?.filter((_: any, i: number) => i !== fieldIndex) || [];
    if (updatedFields.length > 0) {
      onChange({ ...step, fields: updatedFields });
    } else {
      // If no fields left, remove the fields property entirely
      const { fields, ...stepWithoutFields } = step;
      onChange(stepWithoutFields as WorkflowStep);
    }
  };

  const updateFieldType = (fieldIndex: number, type: typeof FIELD_TYPES[number]) => {
    const currentField = step.fields?.[fieldIndex];
    const newField = createFieldByType(type, currentField?.id);
    const updatedFields = [...(step.fields || [])];
    updatedFields[fieldIndex] = newField;
    onChange({ ...step, fields: updatedFields });
  };

  const initializeAgentTask = () => {
    const { field, fields, ...stepWithoutFieldsOrField } = step;
    onChange({
      ...stepWithoutFieldsOrField,
      agentTask: {
        instruction: '',
        outputUI: 'singleChoice',
        context: []
      }
    } as WorkflowStep);
  };

  const toggleMultipleFields = (enabled: boolean) => {
    setUseMultipleFields(enabled);
    if (enabled) {
      // Don't convert if fields already exist
      if (step.fields && step.fields.length > 0) {
        // Just remove the single field property if it exists
        const { field, ...stepWithoutField } = step;
        onChange(stepWithoutField as WorkflowStep);
      } else {
        // Convert single field to fields array
        const initialFields = step.field ? [step.field] : [createFieldByType('textInput', `${step.id}_field1`)];
        const { field, ...stepWithoutField } = step;
        onChange({ ...stepWithoutField, fields: initialFields } as WorkflowStep);
      }
    } else {
      // Convert fields array to single field
      const initialField = step.fields?.[0] || createFieldByType('textInput');
      const { fields, ...stepWithoutFields } = step;
      onChange({ ...stepWithoutFields, field: initialField } as WorkflowStep);
    }
  };

  return (
    <Card className="border-l-4 border-l-primary">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2 flex-1">
            <div {...dragHandleProps} className="cursor-move">
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
            <CollapsibleTrigger asChild>
              <CardTitle className="text-base cursor-pointer hover:underline">
                {step.question || 'Untitled Step'}
              </CardTitle>
            </CollapsibleTrigger>
          </div>
          <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label htmlFor={`${step.id}-id`}>Step ID</Label>
              <Input
                id={`${step.id}-id`}
                value={step.id}
                onChange={(e) => onChange({ ...step, id: e.target.value })}
                placeholder="e.g., selectGoal"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${step.id}-aim`}>Aim</Label>
              <Input
                id={`${step.id}-aim`}
                value={step.aim}
                onChange={(e) => onChange({ ...step, aim: e.target.value })}
                placeholder="Purpose of this step"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${step.id}-question`}>Question</Label>
              <Input
                id={`${step.id}-question`}
                value={step.question}
                onChange={(e) => onChange({ ...step, question: e.target.value })}
                placeholder="Question to ask the user"
              />
            </div>

            {/* Step Image */}
            <div className="space-y-2">
              <Label>Step Image (Optional)</Label>
              <div
                className="flex items-center justify-between p-3 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setIsImageDialogOpen(true)}
              >
                {step.imageUrl ? (
                  <div className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    {typeof step.imageUrl === 'string' ? (
                      <span className="text-sm truncate">{step.imageUrl}</span>
                    ) : (
                      <span className="text-sm">
                        Themed: {step.imageUrl.masterPrompt?.substring(0, 40) || 'Image set'}
                        {step.imageUrl.masterPrompt && step.imageUrl.masterPrompt.length > 40 ? '...' : ''}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Click to add image...</span>
                )}
              </div>
            </div>

            {/* Field Mode Selection */}
            <div className="space-y-2">
              <Label>Field Configuration</Label>
              <Select
                value={fieldMode}
                onValueChange={(value: 'static' | 'agent') => {
                  setFieldMode(value);
                  if (value === 'static' && !step.field && (!step.fields || step.fields.length === 0)) {
                    initializeField('textInput');
                  } else if (value === 'agent' && !step.agentTask) {
                    initializeAgentTask();
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Static Field(s)</SelectItem>
                  <SelectItem value="agent">Agent Generated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Multiple Fields Toggle */}
            {fieldMode === 'static' && (
              <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  id={`${step.id}-multipleFields`}
                  checked={useMultipleFields}
                  onCheckedChange={toggleMultipleFields}
                />
                <Label htmlFor={`${step.id}-multipleFields`}>
                  Use Multiple Fields (all fields displayed on one screen)
                </Label>
              </div>
            )}

            {/* Static Field Configuration */}
            {fieldMode === 'static' && !useMultipleFields && (
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label>Field Type</Label>
                  <Select
                    value={step.field?.type || 'textInput'}
                    onValueChange={(value) => initializeField(value as typeof FIELD_TYPES[number])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {step.field && (
                  <>
                    <div className="space-y-2">
                      <Label>Field ID</Label>
                      <Input
                        value={step.field.id}
                        onChange={(e) => handleFieldChange(0, { id: e.target.value })}
                        placeholder="Field ID for referencing"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Label</Label>
                      <Input
                        value={step.field.label}
                        onChange={(e) => handleFieldChange(0, { label: e.target.value })}
                        placeholder="Field label"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Placeholder</Label>
                      <Input
                        value={step.field.placeholder || ''}
                        onChange={(e) => handleFieldChange(0, { placeholder: e.target.value })}
                        placeholder="Placeholder text"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`${step.id}-required`}
                        checked={step.field.required}
                        onCheckedChange={(checked) => handleFieldChange(0, { required: !!checked })}
                      />
                      <Label htmlFor={`${step.id}-required`}>Required</Label>
                    </div>

                    {/* Choice-based fields */}
                    {(step.field.type === 'singleChoice' || step.field.type === 'multiChoice') && (
                      <>
                        {/* Display Mode for MultiChoice */}
                        {step.field.type === 'multiChoice' && (
                          <div className="space-y-2">
                            <Label>Display Mode</Label>
                            <Select
                              value={step.field.displayMode || 'list'}
                              onValueChange={(value: 'list' | 'grid') => handleFieldChange(0, { displayMode: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="list">List (Checkboxes)</SelectItem>
                                <SelectItem value="grid">Grid (Card Grid)</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              List displays options as checkboxes. Grid displays them as a searchable card grid.
                            </p>
                          </div>
                        )}

                        {/* Min/Max for MultiChoice */}
                        {step.field.type === 'multiChoice' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label>Min Selections</Label>
                              <Input
                                type="number"
                                min="0"
                                value={(step.field as any).min ?? ''}
                                onChange={(e) => handleFieldChange(0, { min: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                                placeholder="No minimum"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Max Selections</Label>
                              <Input
                                type="number"
                                min="1"
                                value={(step.field as any).max ?? ''}
                                onChange={(e) => handleFieldChange(0, { max: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                                placeholder="No maximum"
                              />
                            </div>
                          </div>
                        )}

                        <Collapsible defaultOpen={false}>
                          <div className="flex justify-between items-center">
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="flex items-center gap-1 p-0 h-auto hover:bg-transparent">
                                <ChevronDown className="h-4 w-4 transition-transform [&[data-state=open]>svg]:rotate-180" />
                                <Label className="cursor-pointer">Options ({step.field.options?.length || 0})</Label>
                              </Button>
                            </CollapsibleTrigger>
                            <Button type="button" variant="outline" size="sm" onClick={() => addOption(0)}>
                              <Plus className="h-3 w-3 mr-1" />
                              Add Option
                            </Button>
                          </div>
                          <CollapsibleContent className="mt-2">
                            <div className="space-y-4">
                              {step.field.options?.map((option, idx) => (
                                <Card key={idx} className="p-3">
                                  <div className="space-y-2">
                                    <div className="flex justify-between items-center mb-2">
                                      <h4 className="text-sm font-medium">Option {idx + 1}</h4>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeOption(0, idx)}
                                        className="h-6 w-6 p-0 text-red-600"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Value *</Label>
                                        <Input
                                          placeholder="Value"
                                          value={option.value}
                                          onChange={(e) => updateOption(0, idx, { value: e.target.value })}
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Label *</Label>
                                        <Input
                                          placeholder="Label"
                                          value={option.label}
                                          onChange={(e) => updateOption(0, idx, { label: e.target.value })}
                                        />
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Icon (optional)</Label>
                                        <Input
                                          placeholder="e.g., 🎯 or emoji"
                                          value={option.icon || ''}
                                          onChange={(e) => updateOption(0, idx, { icon: e.target.value })}
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Description (optional)</Label>
                                        <Input
                                          placeholder="Short description"
                                          value={option.description || ''}
                                          onChange={(e) => updateOption(0, idx, { description: e.target.value })}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </>
                    )}

                    {/* DateTime specific */}
                    {step.field.type === 'dateTime' && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`${step.id}-includeTime`}
                          checked={step.field.includeTime}
                          onCheckedChange={(checked) => handleFieldChange(0, { includeTime: !!checked })}
                        />
                        <Label htmlFor={`${step.id}-includeTime`}>Include Time</Label>
                      </div>
                    )}

                    {/* TextInput specific */}
                    {step.field.type === 'textInput' && (
                      <>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`${step.id}-multiline`}
                            checked={step.field.multiline}
                            onCheckedChange={(checked) => handleFieldChange(0, { multiline: !!checked })}
                          />
                          <Label htmlFor={`${step.id}-multiline`}>Multiline</Label>
                        </div>
                        <div className="space-y-2">
                          <Label>Max Length</Label>
                          <Input
                            type="number"
                            value={step.field.maxLength || ''}
                            onChange={(e) => handleFieldChange(0, { maxLength: parseInt(e.target.value) || undefined })}
                            placeholder="Optional max length"
                          />
                        </div>
                      </>
                    )}

                    {/* Number specific */}
                    {step.field.type === 'number' && (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-2">
                            <Label>Min</Label>
                            <Input
                              type="number"
                              value={step.field.min || ''}
                              onChange={(e) => handleFieldChange(0, { min: parseFloat(e.target.value) || undefined })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Max</Label>
                            <Input
                              type="number"
                              value={step.field.max || ''}
                              onChange={(e) => handleFieldChange(0, { max: parseFloat(e.target.value) || undefined })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Step</Label>
                            <Input
                              type="number"
                              value={step.field.step || ''}
                              onChange={(e) => handleFieldChange(0, { step: parseFloat(e.target.value) || undefined })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Display Mode</Label>
                          <Select
                            value={(step.field as any).displayMode || 'compact'}
                            onValueChange={(value: 'slider' | 'picker' | 'compact') => handleFieldChange(0, { displayMode: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="compact">Compact (Inline +/- buttons)</SelectItem>
                              <SelectItem value="picker">Picker (Large +/- buttons)</SelectItem>
                              <SelectItem value="slider">Slider</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Compact is best for mobile. Slider works well for ranges with many values.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Default Value</Label>
                          <Input
                            type="number"
                            value={(step.field as any).defaultValue || ''}
                            onChange={(e) => handleFieldChange(0, { defaultValue: parseFloat(e.target.value) || undefined })}
                            placeholder="Initial value"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Multiple Fields Configuration */}
            {fieldMode === 'static' && useMultipleFields && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Fields (displayed together on one screen)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addNewField}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Field
                  </Button>
                </div>

                {step.fields && step.fields.length > 0 ? (
                  <div className="space-y-4">
                    {step.fields.map((field: FormField, fieldIndex: number) => (
                      <Card key={fieldIndex} className="p-4 border-2">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <h4 className="font-medium">Field {fieldIndex + 1}</h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeField(fieldIndex)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Field Type */}
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                              value={field.type}
                              onValueChange={(value) => updateFieldType(fieldIndex, value as typeof FIELD_TYPES[number])}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FIELD_TYPES.map(type => (
                                  <SelectItem key={type} value={type}>
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Field ID */}
                          <div className="space-y-2">
                            <Label>Field ID *</Label>
                            <Input
                              value={field.id}
                              onChange={(e) => handleFieldChange(fieldIndex, { id: e.target.value })}
                              placeholder="Unique identifier for referencing"
                            />
                            <p className="text-xs text-muted-foreground">
                              Use this ID to reference the field value in transitions and plans
                            </p>
                          </div>

                          {/* Field Label */}
                          <div className="space-y-2">
                            <Label>Label</Label>
                            <Input
                              value={field.label}
                              onChange={(e) => handleFieldChange(fieldIndex, { label: e.target.value })}
                              placeholder="Field label"
                            />
                          </div>

                          {/* Required */}
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`field-${fieldIndex}-required`}
                              checked={field.required}
                              onCheckedChange={(checked) => handleFieldChange(fieldIndex, { required: !!checked })}
                            />
                            <Label htmlFor={`field-${fieldIndex}-required`}>Required</Label>
                          </div>

                          {/* Choice fields - options editor with icon and description */}
                          {(field.type === 'singleChoice' || field.type === 'multiChoice') && (
                            <div className="space-y-3 p-3 bg-muted/30 rounded">
                              {/* Display Mode for MultiChoice */}
                              {field.type === 'multiChoice' && (
                                <>
                                  <div className="space-y-2">
                                    <Label className="text-sm">Display Mode</Label>
                                    <Select
                                      value={(field as any).displayMode || 'list'}
                                      onValueChange={(value: 'list' | 'grid') => handleFieldChange(fieldIndex, { displayMode: value })}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="list">List (Checkboxes)</SelectItem>
                                        <SelectItem value="grid">Grid (Card Grid)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Min Selections</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        value={(field as any).min ?? ''}
                                        onChange={(e) => handleFieldChange(fieldIndex, { min: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                                        placeholder="No minimum"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Max Selections</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={(field as any).max ?? ''}
                                        onChange={(e) => handleFieldChange(fieldIndex, { max: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                                        placeholder="No maximum"
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                              <Collapsible defaultOpen={false}>
                                <div className="flex justify-between items-center">
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="flex items-center gap-1 p-0 h-auto hover:bg-transparent">
                                      <ChevronDown className="h-4 w-4 transition-transform [&[data-state=open]>svg]:rotate-180" />
                                      <Label className="text-sm cursor-pointer">Options ({field.options?.length || 0})</Label>
                                    </Button>
                                  </CollapsibleTrigger>
                                  <Button type="button" variant="outline" size="sm" onClick={() => addOption(fieldIndex)}>
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                </div>
                                <CollapsibleContent className="mt-2 space-y-2">
                                  {field.options?.map((opt: any, optIdx: number) => (
                                    <Card key={optIdx} className="p-3">
                                      <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs font-medium">Option {optIdx + 1}</span>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeOption(fieldIndex, optIdx)}
                                            className="h-6 w-6 p-0 text-red-600"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <Input
                                            placeholder="Value *"
                                            value={opt.value}
                                            onChange={(e) => updateOption(fieldIndex, optIdx, { value: e.target.value })}
                                          />
                                          <Input
                                            placeholder="Label *"
                                            value={opt.label}
                                            onChange={(e) => updateOption(fieldIndex, optIdx, { label: e.target.value })}
                                          />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <Input
                                            placeholder="Icon (e.g., 🎯)"
                                            value={opt.icon || ''}
                                            onChange={(e) => updateOption(fieldIndex, optIdx, { icon: e.target.value })}
                                          />
                                          <Input
                                            placeholder="Description"
                                            value={opt.description || ''}
                                            onChange={(e) => updateOption(fieldIndex, optIdx, { description: e.target.value })}
                                          />
                                        </div>
                                      </div>
                                    </Card>
                                  ))}
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          )}

                          {/* Number field - min/max/step/displayMode/defaultValue */}
                          {field.type === 'number' && (
                            <div className="space-y-3 p-3 bg-muted/30 rounded">
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <Label className="text-xs">Min</Label>
                                  <Input
                                    type="number"
                                    value={field.min || ''}
                                    onChange={(e) => handleFieldChange(fieldIndex, { min: parseFloat(e.target.value) || undefined })}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Max</Label>
                                  <Input
                                    type="number"
                                    value={field.max || ''}
                                    onChange={(e) => handleFieldChange(fieldIndex, { max: parseFloat(e.target.value) || undefined })}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Step</Label>
                                  <Input
                                    type="number"
                                    value={field.step || ''}
                                    onChange={(e) => handleFieldChange(fieldIndex, { step: parseFloat(e.target.value) || undefined })}
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Display Mode</Label>
                                  <Select
                                    value={(field as any).displayMode || 'compact'}
                                    onValueChange={(value: 'slider' | 'picker' | 'compact') => handleFieldChange(fieldIndex, { displayMode: value })}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="compact">Compact (+/-)</SelectItem>
                                      <SelectItem value="picker">Picker (Large)</SelectItem>
                                      <SelectItem value="slider">Slider</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Default Value</Label>
                                  <Input
                                    type="number"
                                    value={(field as any).defaultValue || ''}
                                    onChange={(e) => handleFieldChange(fieldIndex, { defaultValue: parseFloat(e.target.value) || undefined })}
                                    placeholder="Initial value"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground border-2 border-dashed rounded">
                    <p>No fields yet</p>
                    <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addNewField}>
                      Add First Field
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Agent Task Configuration */}
            {fieldMode === 'agent' && step.agentTask && (
              <div className="space-y-4 p-4 border rounded-lg bg-blue-50/50">
                <div className="space-y-2">
                  <Label>Instruction Template</Label>
                  <Textarea
                    value={step.agentTask.instruction}
                    onChange={(e) => handleAgentTaskChange({ instruction: e.target.value })}
                    placeholder="Instruction for the agent"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Output UI Type</Label>
                  <Select
                    value={step.agentTask.outputUI}
                    onValueChange={(value) => handleAgentTaskChange({ outputUI: value as any })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Context (Step IDs)</Label>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      if (!step.agentTask!.context.includes(value)) {
                        handleAgentTaskChange({
                          context: [...step.agentTask!.context, value]
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Add context step" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSteps.map(stepId => (
                        <SelectItem key={stepId} value={stepId}>
                          {stepId}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {step.agentTask.context.map((ctx, idx) => (
                      <div key={idx} className="flex items-center gap-1 bg-white border rounded px-2 py-1">
                        <span className="text-sm">{ctx}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => {
                            handleAgentTaskChange({
                              context: step.agentTask!.context.filter((_, i) => i !== idx)
                            });
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Force Agent */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${step.id}-forceAgent`}
                checked={step.forceAgent}
                onCheckedChange={(checked) => onChange({ ...step, forceAgent: !!checked })}
              />
              <Label htmlFor={`${step.id}-forceAgent`}>Force Agent for this step</Label>
            </div>

            {/* Transitions */}
            <TransitionEditor
              transition={step.transition}
              onChange={(transition) => onChange({ ...step, transition })}
              fieldOptions={
                (step.field?.type === 'singleChoice' || step.field?.type === 'multiChoice') && step.field.options
                  ? step.field.options.map(opt => ({ value: opt.value || '', label: opt.label || '' }))
                  : []
              }
              availableWorkflows={availableWorkflows}
            />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      {/* Image Generation Dialog */}
      <ImageGenerationDialog
        open={isImageDialogOpen}
        onOpenChange={setIsImageDialogOpen}
        currentImage={step.imageUrl}
        onImageSelect={(image) => onChange({ ...step, imageUrl: image })}
        title={`Image for ${step.question || step.id}`}
      />
    </Card>
  );
};
